import type { ReactNode } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { XIcon } from '@/components/layout/icons';

type Props = {
  readonly name: string;
  /** A second line under the title — the lane view names its machine here (OPS-128). */
  readonly subtitle?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
};

/**
 * The fleet's side panel (OPS-128): the catalog `SidePanel` at 480px from the
 * right, laid OVER the table rather than beside it — no docking, no backdrop,
 * so the table left of it stays clickable and swaps what it shows. Narrower
 * than that, it takes the full width. Details only: every action is on its row.
 *
 * Not a dialog: it does not trap focus or block the page, so it is a labelled
 * `aside`. The confirm dialogs it opens stack above it (`z-50`).
 */
export function MachinePanel({ name, subtitle, onClose, children }: Props) {
  const t = useTranslations('pages.admin.fleet');

  return (
    <aside
      aria-label={name}
      className="absolute inset-y-0 right-0 z-10 flex w-full max-w-[480px] flex-col border-l border-border bg-surface shadow-xl"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-neutral-100 px-6 py-4">
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 className="truncate text-lg font-semibold text-text-primary">{name}</h2>
          {subtitle !== undefined && <span className="truncate text-sm text-text-tertiary">{subtitle}</span>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('panel-close')}
          title={t('panel-close')}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg"
        >
          <XIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {/* One scroll context, the panel's own. */}
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </aside>
  );
}
